import { OjWebElement } from '@oracle/oraclejet-webdriver/elements';

/**
 * This is the base class for oj-c-meter-bar WebElement, and is generated from the
 * component's metadata. Do not modify these contents since they'll be replaced
 * during the next generation.
 * Put overrides into the WebElements's subclass, MeterBarWebElement.ts.
 */
export class MeterBarWebElementBase extends OjWebElement {
  /**
   * Gets the value of <code>max</code> property.
   * The maximum value of the meter bar.
   * @return The value of <code>max</code> property.
   *
   */
  public getMax(): Promise<number> {
    return this.getProperty<number>('max');
  }

  /**
   * Gets the value of <code>min</code> property.
   * The minimum value of the meter bar.
   * @return The value of <code>min</code> property.
   *
   */
  public getMin(): Promise<number> {
    return this.getProperty<number>('min');
  }

  /**
   * Gets the value of <code>readonly</code> property.
   *
   * @return The value of <code>readonly</code> property.
   *
   */
  public getReadonly(): Promise<boolean> {
    return this.getProperty<boolean>('readonly');
  }

  /**
   * Sets the value of <code>value</code> property.
   * The value of the meter bar.
   * @param value The value to set for <code>value</code>
   *
   */
  public changeValue(value: number | null): Promise<void> {
    return this.setProperty<number | null>('value', value);
  }

  /**
   * Gets the value of <code>value</code> property.
   * The value of the meter bar.
   * @return The value of <code>value</code> property.
   *
   */
  public getValue(): Promise<number | null> {
    return this.getProperty<number | null>('value');
  }

  /**
   * Gets the value of <code>baseline</code> property.
   * Define the baseline value of the bar. If undefined, defaults to minimum value of the meter bar.
   * @return The value of <code>baseline</code> property.
   *
   */
  public getBaseline(): Promise<number> {
    return this.getProperty<number>('baseline');
  }

  /**
   * Gets the value of <code>step</code> property.
   *
   * @return The value of <code>step</code> property.
   *
   */
  public getStep(): Promise<number> {
    return this.getProperty<number>('step');
  }

  /**
   * Gets the value of <code>color</code> property.
   *
   * @return The value of <code>color</code> property.
   *
   */
  public getColor(): Promise<string> {
    return this.getProperty<string>('color');
  }

  /**
   * Gets the value of <code>indicatorSize</code> property.
   *
   * @return The value of <code>indicatorSize</code> property.
   *
   */
  public getIndicatorSize(): Promise<number> {
    return this.getProperty<number>('indicatorSize');
  }

  /**
   * Gets the value of <code>plotArea</code> property.
   *
   * @return The value of <code>plotArea</code> property.
   *
   */
  public getPlotArea(): Promise<PlotArea> {
    return this.getProperty<PlotArea>('plotArea');
  }

  /**
   * Gets the value of <code>orientation</code> property.
   *
   * @return The value of <code>orientation</code> property.
   *
   */
  public getOrientation(): Promise<string> {
    return this.getProperty<string>('orientation');
  }

  /**
   * Gets the value of <code>referenceLines</code> property.
   *
   * @return The value of <code>referenceLines</code> property.
   *
   */
  public getReferenceLines(): Promise<Array<ReferenceLines>> {
    return this.getProperty<Array<ReferenceLines>>('referenceLines');
  }

  /**
   * Gets the value of <code>thresholdDisplay</code> property.
   *
   * @return The value of <code>thresholdDisplay</code> property.
   *
   */
  public getThresholdDisplay(): Promise<string> {
    return this.getProperty<string>('thresholdDisplay');
  }

  /**
   * Gets the value of <code>thresholds</code> property.
   *
   * @return The value of <code>thresholds</code> property.
   *
   */
  public getThresholds(): Promise<Array<Thresholds>> {
    return this.getProperty<Array<Thresholds>>('thresholds');
  }

  /**
   * Gets the value of <code>describedBy</code> property.
   *
   * @return The value of <code>describedBy</code> property.
   *
   */
  public getDescribedBy(): Promise<string | null> {
    return this.getProperty<string | null>('describedBy');
  }

  /**
   * Gets the value of <code>labelledBy</code> property.
   *
   * @return The value of <code>labelledBy</code> property.
   *
   */
  public getLabelledBy(): Promise<string | null> {
    return this.getProperty<string | null>('labelledBy');
  }

  /**
   * Gets the value of <code>size</code> property.
   * Specifies the size of the meter bar.
   * @return The value of <code>size</code> property.
   *
   */
  public getSizeProperty(): Promise<string> {
    return this.getProperty<string>('size');
  }

  /**
   * Gets the value of <code>datatip</code> property.
   *
   * @return The value of <code>datatip</code> property.
   *
   */
  public getDatatip(): Promise<null> {
    return this.getProperty<null>('datatip');
  }

  /**
   * Gets the value of <code>transientValue</code> property.
   *
   * @return The value of <code>transientValue</code> property.
   *
   */
  public getTransientValue(): Promise<number> {
    return this.getProperty<number>('transientValue');
  }
}
export interface PlotArea {
  /**
   *
   */
  color: string;
  /**
   *
   */
  rendered: string;
}
export interface ReferenceLines {
  /**
   *
   */
  color: string;
  /**
   *
   */
  position: string;
  /**
   *
   */
  value: number;
  /**
   *
   */
  label: string;
  /**
   *
   */
  style: ReferenceLinesStyle;
}
export interface ReferenceLinesStyle {
  /**
   *
   */
  color: string;
  /**
   *
   */
  fontSize: string;
  /**
   *
   */
  fontStyle: string;
  /**
   *
   */
  fontWeight: string;
}
export interface Thresholds {
  /**
   *
   */
  accessibleLabel: string;
  /**
   *
   */
  color: string;
  /**
   *
   */
  max: number;
}
